/* Issues directory — the Jira-lite retrieval surface for the Admin desk.
   Search + filter (status/severity/state/type/assignee/label) + sortable
   columns over ALL issues (past + present), paginated. Every control maps 1:1
   to window.MAV.queryIssues(params), which is the exact shape the backend will
   serve from GET /issues — so wiring the API is a drop-in replacement.
   Clicking a row opens the existing IssueTicket via onOpenIssue(id). */

const { useState: useDirState, useMemo: useDirMemo } = React;

const DIR_STATE = {
  triage:      { pill: 'pill-queued', label: 'Triage' },
  in_progress: { pill: 'pill-run',    label: 'In progress' },
  in_review:   { pill: 'pill-cond',   label: 'In review' },
  resolved:    { pill: 'pill-pass',   label: 'Resolved' },
};
const DIR_SEV = { blocker: 'Blocker', high: 'High', medium: 'Medium', low: 'Low' };

function DirAvatar({ id, size = 22 }) {
  const m = window.MAV.memberById(id);
  if (!m) return <span className="avatar" style={{ width: size, height: size, fontSize: size * 0.4, background: 'var(--queued)' }}>·</span>;
  return <span className="avatar" style={{ width: size, height: size, fontSize: size * 0.4, background: m.color }}>{m.initials}</span>;
}

function dirFmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso); if (isNaN(d)) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function IssuesDirectory({ onOpenIssue }) {
  const MAV = window.MAV;
  const [q, setQ] = useDirState('');
  const [status, setStatus] = useDirState('all');
  const [severity, setSeverity] = useDirState('all');
  const [state, setState] = useDirState('all');
  const [type, setType] = useDirState('all');
  const [assignee, setAssignee] = useDirState('all');
  const [label, setLabel] = useDirState('all');
  const [sort, setSort] = useDirState('created');
  const [dir, setDir] = useDirState('desc');

  const params = { q, status, severity, state, type, assignee, label, sort, dir, page: 1, pageSize: 100 };
  const res = useDirMemo(() => MAV.queryIssues(params), [q, status, severity, state, type, assignee, label, sort, dir]);
  const { rows, total, facets } = res;

  const setCol = (col) => {
    if (sort === col) setDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSort(col); setDir(col === 'title' || col === 'id' ? 'asc' : 'desc'); }
  };
  const caret = (col) => sort === col ? (dir === 'asc' ? ' ↑' : ' ↓') : '';
  const reset = () => { setQ(''); setStatus('all'); setSeverity('all'); setState('all'); setType('all'); setAssignee('all'); setLabel('all'); setSort('created'); setDir('desc'); };
  const anyFilter = q || status !== 'all' || severity !== 'all' || state !== 'all' || type !== 'all' || assignee !== 'all' || label !== 'all';

  const now = MAV.now;
  const Sel = ({ value, onChange, children, w }) => (
    <select className="input dir-sel" value={value} onChange={e => onChange(e.target.value)} style={w ? { width: w } : null}>{children}</select>
  );

  return (
    <div className="fade-in">
      {/* toolbar */}
      <div className="dir-toolbar">
        <div className="dir-search">
          <I.search size={15} />
          <input placeholder="Search issues — key, title, build, label…" value={q} onChange={e => setQ(e.target.value)} />
          {q && <button className="dir-clear" onClick={() => setQ('')} title="Clear"><I.x size={13} /></button>}
        </div>
        <div className="dir-filters">
          <Sel value={status} onChange={setStatus}>
            <option value="all">All statuses</option>
            <option value="open">Open ({facets.status.open})</option>
            <option value="resolved">Resolved ({facets.status.resolved})</option>
          </Sel>
          <Sel value={severity} onChange={setSeverity}>
            <option value="all">All severities</option>
            {['blocker', 'high', 'medium', 'low'].map(s => <option key={s} value={s}>{DIR_SEV[s]} ({facets.severity[s]})</option>)}
          </Sel>
          <Sel value={state} onChange={setState}>
            <option value="all">All states</option>
            {Object.keys(DIR_STATE).map(s => <option key={s} value={s}>{DIR_STATE[s].label}</option>)}
          </Sel>
          <Sel value={type} onChange={setType}>
            <option value="all">All types</option>
            <option value="Bug">Bug</option>
            <option value="Feature request">Feature request</option>
          </Sel>
          <Sel value={assignee} onChange={setAssignee}>
            <option value="all">Any assignee</option>
            <option value="unassigned">Unassigned</option>
            {MAV.team.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </Sel>
          <Sel value={label} onChange={setLabel}>
            <option value="all">All labels</option>
            {facets.label.map(l => <option key={l} value={l}>{l}</option>)}
          </Sel>
          {anyFilter && <button className="btn btn-quiet dir-reset" onClick={reset}><I.retry size={13} /> Reset</button>}
        </div>
      </div>

      <div className="dir-meta">
        <span><b style={{ color: 'var(--ink)' }}>{total}</b> {total === 1 ? 'issue' : 'issues'}{anyFilter ? ' match' : ' total'} · {facets.status.open} open, {facets.status.resolved} resolved</span>
      </div>

      {/* directory table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="dir-head">
          <button className="dir-h dir-h-key" onClick={() => setCol('id')}>Key{caret('id')}</button>
          <button className="dir-h dir-h-title" onClick={() => setCol('title')}>Summary{caret('title')}</button>
          <button className="dir-h dir-h-sev" onClick={() => setCol('severity')}>Sev{caret('severity')}</button>
          <span className="dir-h">State</span>
          <span className="dir-h dir-h-asg">Assignee</span>
          <button className="dir-h dir-h-created" onClick={() => setCol('created')}>Created{caret('created')}</button>
          <button className="dir-h dir-h-due" onClick={() => setCol('due')}>Due{caret('due')}</button>
        </div>
        {rows.length === 0 ? (
          <div className="dir-empty">
            <I.search size={22} />
            <p style={{ fontWeight: 700, margin: '8px 0 2px' }}>No issues match</p>
            <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>Try clearing a filter or the search.</p>
          </div>
        ) : rows.map(it => {
          const asg = MAV.memberById(it.assignee);
          const overdue = it.status === 'open' && it.due && new Date(it.due) < now;
          const st = DIR_STATE[it.state] || DIR_STATE.triage;
          return (
            <button key={it.id} className="dir-row" onClick={() => onOpenIssue && onOpenIssue(it.id)}>
              <span className="dir-key mono">
                <I.warn size={11} style={{ color: it.type === 'Feature request' ? 'var(--running)' : 'var(--muted)', flexShrink: 0 }} />
                {it.id}
              </span>
              <span className="dir-title">
                <span className="dir-title-t">{it.title || it.text}</span>
                <span className="dir-title-sub">
                  <span className="mono">{it.build}</span>
                  {(it.labels || []).slice(0, 3).map(l => <span key={l} className="dir-lbl">{l}</span>)}
                </span>
              </span>
              <span className={'sev-tag sev-' + it.sev} title={DIR_SEV[it.sev] + ' severity'}>{DIR_SEV[it.sev]}</span>
              <span className="dir-cell">
                <span className={'pill ' + st.pill} style={{ fontSize: 9.5 }}>{it.state === 'resolved' ? <span className="dot"></span> : null}{st.label}</span>
              </span>
              <span className="dir-cell dir-asg">
                {asg ? <React.Fragment><DirAvatar id={it.assignee} size={20} /><span className="dir-asg-n">{asg.name}</span></React.Fragment>
                  : <span className="muted" style={{ fontSize: 11.5 }}>Unassigned</span>}
              </span>
              <span className="dir-cell mono dir-date dir-col-created">{dirFmtDate(it.created)}</span>
              <span className={'dir-cell mono dir-date' + (overdue ? ' dir-overdue' : '')}>{it.status === 'resolved' ? '—' : dirFmtDate(it.due)}{overdue ? ' !' : ''}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

window.IssuesDirectory = IssuesDirectory;
